import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import AppShell from '../../components/layout/AppShell';
import PageHeader from '../../components/layout/PageHeader';
import TableTabsPanel from '../../components/POS/TableTabsPanel';
import MenuGrid from '../../components/POS/MenuGrid';
import AddonDialog from '../../components/POS/AddonDialog';
import CartPanel from '../../components/POS/CartPanel';
import PaymentDialog from '../../components/POS/PaymentDialog';
import ReceiptDialog from '../../components/POS/ReceiptDialog';
import { useAuth } from '../../context/AuthContext';
import useOrdersSocket from '../../hooks/useOrdersSocket';
import { toast } from '../../lib/toast';
import { newClientRequestId } from '../../utils/clientRequestId';
import {
  getRestaurant,
  getRestaurants,
  getMenuItemsAll,
  getMenuItemAddons,
  listOpenTabs,
  getTab,
  openTab as apiOpenTab,
  addOrderToTab,
  closeTab as apiCloseTab,
} from '../../services/api';

const addonsKeyOf = (selectedAddons) =>
  selectedAddons && selectedAddons.length
    ? JSON.stringify(selectedAddons.map((a) => ({ c: a.category, o: a.option, p: a.price })))
    : '';

const POS = () => {
  const { t } = useTranslation();
  const { restaurants, isAdmin } = useAuth();

  const [restaurantList, setRestaurantList] = useState(restaurants || []);
  const [restaurantId, setRestaurantId] = useState(restaurants?.[0]?.id || null);
  const [currency, setCurrency] = useState(null);

  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const addonCache = useRef({});
  // Monotonic counter for stable cart-line keys, so removing a line can't make
  // React reuse another line's DOM node (qty/focus glitches).
  const cartUidRef = useRef(0);

  const [openTabs, setOpenTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [cart, setCart] = useState([]);
  const [opening, setOpening] = useState(false);
  const [sending, setSending] = useState(false);

  const [addonDialog, setAddonDialog] = useState({ open: false, item: null, addons: [] });
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);

  // Resolve which restaurants this user can run a POS for.
  useEffect(() => {
    if (restaurants && restaurants.length) {
      setRestaurantList(restaurants);
      setRestaurantId((prev) => prev || restaurants[0].id);
    } else if (isAdmin?.()) {
      getRestaurants()
        .then((res) => {
          const list = res.data || [];
          setRestaurantList(list);
          setRestaurantId((prev) => prev || list[0]?.id || null);
        })
        .catch(() => {});
    }
  }, [restaurants, isAdmin]);

  // Load restaurant currency + menu + open tabs whenever the restaurant changes.
  useEffect(() => {
    if (!restaurantId) return;
    setActiveTabId(null);
    setActiveTab(null);
    setCart([]);
    addonCache.current = {};

    getRestaurant(restaurantId)
      .then((res) => {
        const r = res.data || {};
        setCurrency({ code: r.currency_code, symbol: r.currency_symbol, position: r.currency_position });
      })
      .catch(() => setCurrency(null));

    setMenuLoading(true);
    getMenuItemsAll(restaurantId)
      .then((res) => setMenuItems(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMenuItems([]))
      .finally(() => setMenuLoading(false));

    refreshTabs(restaurantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const refreshTabs = useCallback((rid = restaurantId) => {
    if (!rid) return;
    listOpenTabs(rid)
      .then((res) => setOpenTabs(res.data?.tabs || []))
      .catch(() => {});
  }, [restaurantId]);

  const loadActiveTab = useCallback((id = activeTabId, rid = restaurantId) => {
    if (!id || !rid) return;
    getTab(rid, id)
      .then((res) => setActiveTab({ ...res.data.tab, financials: res.data.financials }))
      .catch(() => {});
  }, [activeTabId, restaurantId]);

  useEffect(() => {
    if (activeTabId) loadActiveTab(activeTabId);
    else setActiveTab(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // Coalesce socket-driven reloads: a burst of order/payment events (e.g. several
  // cashiers sending at once) collapses into a single refresh instead of one
  // refreshTabs()+loadActiveTab() per event. Refs keep the timer reading current
  // state/handlers without re-subscribing the socket.
  const refreshTabsRef = useRef(refreshTabs);
  const loadActiveTabRef = useRef(loadActiveTab);
  const activeTabIdRef = useRef(activeTabId);
  useEffect(() => { refreshTabsRef.current = refreshTabs; }, [refreshTabs]);
  useEffect(() => { loadActiveTabRef.current = loadActiveTab; }, [loadActiveTab]);
  useEffect(() => { activeTabIdRef.current = activeTabId; }, [activeTabId]);

  const refreshTimerRef = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTabsRef.current();
      if (activeTabIdRef.current) loadActiveTabRef.current(activeTabIdRef.current);
    }, 400);
  }, []);
  useEffect(() => () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); }, []);

  // Pub/sub for pos_payment_updated, so the PaymentDialog's card flow can resolve
  // on the realtime event (delivered through the single page socket) instead of
  // polling the payment row.
  const paymentListenersRef = useRef(new Set());
  const subscribeToPaymentEvents = useCallback((listener) => {
    paymentListenersRef.current.add(listener);
    return () => { paymentListenersRef.current.delete(listener); };
  }, []);

  // Live refresh when orders/payments change (also keeps multiple cashiers in sync).
  useOrdersSocket(restaurantId, (evt) => {
    if (evt?.type === 'payment' && evt.data) {
      paymentListenersRef.current.forEach((fn) => { try { fn(evt.data); } catch (_) {} });
    }
    scheduleRefresh();
  });

  // --- Tabs ---
  const handleOpenTab = async (tableNumber) => {
    setOpening(true);
    try {
      const res = await apiOpenTab(restaurantId, tableNumber);
      setActiveTabId(res.data.id);
      refreshTabs();
      if (res.data.existing) toast(t('pos.tabs.alreadyOpen', 'That table already had an open tab.'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('pos.tabs.openFailed', 'Could not open tab.'));
    } finally {
      setOpening(false);
    }
  };

  // --- Menu / cart ---
  // Stable identities so the memoized MenuGrid cells don't re-render on cart changes.
  const addToCart = useCallback((cartItem) => {
    const key = addonsKeyOf(cartItem.selectedAddons);
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.id === cartItem.id && addonsKeyOf(i.selectedAddons) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + (cartItem.quantity || 1) };
        return next;
      }
      return [...prev, { ...cartItem, quantity: cartItem.quantity || 1, uid: ++cartUidRef.current }];
    });
  }, []);

  const handlePickItem = useCallback(async (item) => {
    let addons = addonCache.current[item.id];
    if (!addons) {
      try {
        const res = await getMenuItemAddons(item.id);
        addons = Array.isArray(res.data) ? res.data : [];
      } catch (_) {
        addons = [];
      }
      addonCache.current[item.id] = addons;
    }
    if (addons.length > 0) {
      setAddonDialog({ open: true, item, addons });
    } else {
      addToCart({ ...item, quantity: 1, price: parseFloat(item.price), selectedAddons: [] });
    }
  }, [addToCart]);

  const changeQty = (index, delta) => {
    setCart((prev) => {
      const next = [...prev];
      const q = next[index].quantity + delta;
      if (q < 1) return prev;
      next[index] = { ...next[index], quantity: q };
      return next;
    });
  };

  const removeItem = (index) => setCart((prev) => prev.filter((_, i) => i !== index));

  const handleSend = async () => {
    if (!activeTabId || cart.length === 0) return;
    setSending(true);
    // One idempotency key per send attempt: a retry of the same submission can't
    // create a duplicate order server-side.
    const clientRequestId = newClientRequestId();
    try {
      const items = cart.map((c) => ({
        id: c.id,
        quantity: c.quantity,
        price: c.price,
        selectedAddons: c.selectedAddons || [],
      }));
      await addOrderToTab(restaurantId, activeTabId, { items, client_request_id: clientRequestId });
      setCart([]);
      toast.success(t('pos.cart.sent', 'Order sent to the kitchen'));
      loadActiveTab(activeTabId);
      refreshTabs();
    } catch (err) {
      toast.error(err.response?.data?.error || t('pos.cart.sendFailed', 'Could not send order.'));
    } finally {
      setSending(false);
    }
  };

  // --- Settle ---
  const handlePaid = () => {
    setPaymentOpen(false);
    loadActiveTab(activeTabId);
    refreshTabs();
  };

  const handleCloseTab = async () => {
    if (!activeTabId) return;
    try {
      await apiCloseTab(restaurantId, activeTabId);
      toast.success(t('pos.cart.closed', 'Tab settled and closed'));
      setActiveTabId(null);
      setActiveTab(null);
      setCart([]);
      refreshTabs();
    } catch (err) {
      toast.error(err.response?.data?.error || t('pos.cart.closeFailed', 'Could not close tab.'));
    }
  };

  const restaurantSelector = restaurantList.length > 1 ? (
    <Select
      size="small"
      value={restaurantId || ''}
      onChange={(e) => setRestaurantId(e.target.value)}
      sx={{ minWidth: 200 }}
    >
      {restaurantList.map((r) => (
        <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
      ))}
    </Select>
  ) : null;

  return (
    <AppShell maxWidth={false}>
      <PageHeader
        title={t('pos.title', 'Point of Sale')}
        subtitle={t('pos.subtitle', 'Take orders, manage tabs and take payment from anywhere.')}
        action={restaurantSelector}
      />

      {!restaurantId ? (
        <Typography sx={{ color: 'text.secondary' }}>
          {t('pos.noRestaurant', 'No restaurant available for point of sale.')}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '280px 1fr', lg: '280px 1fr 340px' },
            gap: 2,
            height: { md: 'calc(100vh - 220px)' },
            minHeight: 480,
          }}
        >
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2, backgroundColor: 'background.paper', minHeight: 0 }}>
            <TableTabsPanel
              tabs={openTabs}
              activeTabId={activeTabId}
              onSelectTab={setActiveTabId}
              onOpenTab={handleOpenTab}
              currency={currency}
              opening={opening}
            />
          </Box>

          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2, backgroundColor: 'background.paper', minHeight: 0 }}>
            <MenuGrid
              items={menuItems}
              currency={currency}
              onPick={handlePickItem}
              disabled={!activeTabId}
              loading={menuLoading}
            />
          </Box>

          <Box sx={{ gridColumn: { xs: '1', md: '1 / -1', lg: 'auto' }, border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2, backgroundColor: 'background.paper', minHeight: 0 }}>
            <CartPanel
              cart={cart}
              currency={currency}
              activeTab={activeTab}
              onChangeQty={changeQty}
              onRemove={removeItem}
              onSend={handleSend}
              onCharge={() => setPaymentOpen(true)}
              onReceipt={() => setReceiptOpen(true)}
              onCloseTab={handleCloseTab}
              sending={sending}
            />
          </Box>
        </Box>
      )}

      <AddonDialog
        open={addonDialog.open}
        item={addonDialog.item}
        addons={addonDialog.addons}
        currency={currency}
        onClose={() => setAddonDialog({ open: false, item: null, addons: [] })}
        onConfirm={(cartItem) => {
          addToCart(cartItem);
          setAddonDialog({ open: false, item: null, addons: [] });
        }}
      />

      {activeTab && (
        <PaymentDialog
          open={paymentOpen}
          restaurantId={restaurantId}
          sessionId={activeTabId}
          owed={activeTab.financials?.owed ?? 0}
          currency={currency}
          onClose={() => setPaymentOpen(false)}
          onPaid={handlePaid}
          onSubscribePayments={subscribeToPaymentEvents}
        />
      )}

      <ReceiptDialog
        open={receiptOpen}
        restaurantId={restaurantId}
        sessionId={activeTabId}
        currency={currency}
        onClose={() => setReceiptOpen(false)}
      />
    </AppShell>
  );
};

export default POS;
